import { Link } from "react-router-dom";
import { Heart, MapPin, Zap, Star, GitCompare, ShieldCheck, Clock, AlertTriangle, Gem, Sparkles } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompare } from "@/lib/useCompare";
import { resolveImageUrl } from "@/lib/img";

const VERIFICATION_BADGE = {
  oem_verified:    { label: "Verified OEM Offer",    cls: "bg-emerald-500/20 border-emerald-400/40 text-emerald-300", icon: ShieldCheck },
  dealer_verified: { label: "Verified Dealer Offer", cls: "bg-blue-500/20 border-blue-400/40 text-blue-300",       icon: ShieldCheck },
  admin_verified:  { label: "Admin Verified",        cls: "bg-violet-500/20 border-violet-400/40 text-violet-300", icon: ShieldCheck },
  pending_verification: { label: "Pending Verification", cls: "bg-yellow-500/20 border-yellow-400/40 text-yellow-300", icon: Clock },
  expired:         { label: "Expired",               cls: "bg-gray-500/20 border-gray-400/40 text-gray-300",       icon: AlertTriangle },
  rejected:        { label: "Rejected",              cls: "bg-red-500/20 border-red-400/40 text-red-300",          icon: AlertTriangle },
};

const INTEL_BADGE = {
  great:   { label: "Great Deal",   cls: "bg-emerald-500/30 border-emerald-400/60 text-emerald-200" },
  good:    { label: "Good Deal",    cls: "bg-blue-500/25 border-blue-400/50 text-blue-200" },
  average: { label: "Average Deal", cls: "bg-gray-500/25 border-gray-400/40 text-gray-200" },
};

export default function LeaseDealCard({ deal, onToggleFav, favorited: favProp }) {
  const { user } = useAuth();
  const { ids: compareIds, toggle: toggleCompare } = useCompare();
  const [fav, setFav] = useState(!!favProp);
  const [busy, setBusy] = useState(false);
  const isComparing = compareIds.includes(deal.id);
  const vbadge = VERIFICATION_BADGE[deal.verification_status] || VERIFICATION_BADGE.pending_verification;
  const VIcon = vbadge.icon;
  const imgSrc = resolveImageUrl(deal.image_url);

  const usd = (n) => `$${Math.round(n).toLocaleString()}`;

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      window.location.href = "/login";
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/favorites/${deal.id}`);
      setFav(data.favorited);
      onToggleFav?.(deal.id, data.favorited);
    } finally { setBusy(false); }
  };

  const onCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleCompare(deal.id);
  };

  return (
    <Link
      to={`/lease-deals/${deal.id}`}
      data-testid={`deal-card-${deal.id}`}
      className="group block bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden hover-lift hover:border-white/20"
    >
      {/* image */}
      <div className="relative aspect-[16/10] overflow-hidden bg-[#12121A]">
        <img
          src={imgSrc}
          alt={`${deal.brand} ${deal.model}`}
          loading="lazy"
          onError={(e) => { e.currentTarget.src = "/placeholder-vehicle.svg"; }}
          className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0E] via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          {INTEL_BADGE[deal.intelligence_score] && (
            <span className={`px-2 py-1 rounded-full border ${INTEL_BADGE[deal.intelligence_score].cls} text-[9px] font-black tracking-widest flex items-center gap-1`} data-testid={`deal-intel-${deal.id}`} title={deal.intelligence_reason || ""}>
              <Sparkles className="w-2.5 h-2.5" /> {INTEL_BADGE[deal.intelligence_score].label.toUpperCase()}
            </span>
          )}
          {deal.featured && (
            <span className="px-2 py-1 rounded-full silver-gradient text-[#050507] text-[9px] font-black tracking-widest flex items-center gap-1">
              <Star className="w-2.5 h-2.5" /> FEATURED
            </span>
          )}
          {deal.is_ev && (
            <span className="px-2 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[9px] font-bold tracking-widest flex items-center gap-1">
              <Zap className="w-2.5 h-2.5" /> ELECTRIC
            </span>
          )}
        </div>
        <button
          onClick={toggle}
          disabled={busy}
          aria-label="Favorite"
          data-testid={`deal-fav-${deal.id}`}
          className="absolute top-3 right-3 w-9 h-9 rounded-full glass-strong flex items-center justify-center hover:scale-110 transition"
        >
          <Heart className={`w-4 h-4 ${fav ? "fill-white text-white" : "text-white"}`} />
        </button>
        <button
          onClick={onCompare}
          aria-label="Compare"
          data-testid={`deal-compare-${deal.id}`}
          className={`absolute top-14 right-3 w-9 h-9 rounded-full flex items-center justify-center transition hover:scale-110 ${isComparing ? "silver-gradient text-[#050507]" : "glass-strong text-white"}`}
        >
          <GitCompare className="w-4 h-4" />
        </button>
      </div>

      {/* body */}
      <div className="p-5">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div>
            <div className="label-mono">{deal.brand} · {deal.year}</div>
            <div className="font-display text-xl font-light tracking-tight mt-1">{deal.model}</div>
            <div className="text-xs text-gray-500 mt-0.5">{deal.trim}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] text-gray-500">MSRP</div>
            <div className="text-sm text-gray-300">${(deal.msrp / 1000).toFixed(1)}k</div>
          </div>
        </div>

        <div className="border-t border-white/5 pt-4 grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="font-display text-2xl font-light">{usd(deal.monthly_payment)}</div>
            <div className="text-[10px] text-gray-500 tracking-wider uppercase mt-0.5">/mo</div>
          </div>
          <div>
            <div className="font-display text-sm text-gray-200">{usd(deal.due_at_signing)}</div>
            <div className="text-[10px] text-gray-500 tracking-wider uppercase mt-0.5">Due Signing</div>
          </div>
          <div>
            <div className="font-display text-sm text-gray-200">{deal.lease_term_months}/{deal.annual_mileage / 1000}k</div>
            <div className="text-[10px] text-gray-500 tracking-wider uppercase mt-0.5">Term/Mi</div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-[11px] text-gray-500">
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {deal.region}</span>
          <span className="text-emerald-400">Save ~{usd(deal.savings || 0)}</span>
        </div>

        <div className="mt-4 silver-gradient text-[#050507] text-center py-3 rounded-full font-bold text-[12px] tracking-wide group-hover:opacity-90">
          GET THIS DEAL
        </div>
      </div>
    </Link>
  );
}
